import React, { useEffect, useRef, useState } from 'react';
// import { appInsights, reactPlugin } from '../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Modal from '@mui/material/Modal';
import { jsonEditorStyles } from './jsonEditorStyles';
import cn from 'clsx';
import Button from '@mui/material/Button';
import { Box, Typography, TextField, Link, AccordionSummary, Accordion, AccordionDetails, FormControlLabel, Checkbox, SelectChangeEvent, InputLabel, FormControl, OutlinedInput, Select, MenuItem, ListItemText } from '@mui/material';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { getJsonEditor, getSessionId, getTemplatizationEnvironments } from '../../store';
import jsonConstants from '../../constants/jsonConstants';
import { generatePolicyXML, RelyingPartyReference } from '../../jsonParsing/policyXMLGeneration';
import { BuildSettings, extractXMLGenerationSettings, generateAppSettingsJsonString } from '../../jsonParsing/utils';
import { ExpandMore } from '@material-ui/icons';
import { SelectComponent, TooltipComponent } from '../uiComponents';
import { cloneDeep } from 'lodash';
import { OptionProps } from '../../view-models';
import { validationResultsContainErrors, envExists, envHasEmptySettings } from './utils';

interface PolicyDownloadUIModalProps {
    closeComponent: Function,
    enableJavascript: boolean,
    setEnableJavascript: Function,
    journeyInsightsSettings: any,
    setJourneyInsightsSettings: Function,
    templatizedDownload: boolean,
    setTemplatizedDownload: Function,
    envsForAppSettings: { [index: string]: any },
    setEnvsForAppSettings: Function,
    downloadEnv: string,
    setDownloadEnv: Function
}

const executePolicyDownload = (dlElements: string[], setDlElements: Function, dlParentRef: any, editorState: any, sessionId: string, templatizedDownload: boolean, downloadEnv: string, envsToInclude: string[], enableJavascript: boolean, journeyInsightsSettings: any) => {
    let rawXMLoutput: {
        policyXML: string;
        relyingParties: RelyingPartyReference[];
        testNowUris: string[];
        allPolicyNames: string[];
        buildSettings: BuildSettings;
      } | null = null;
    try {
        rawXMLoutput = generatePolicyXML(editorState as { [id: string]: any }, sessionId, { ...extractXMLGenerationSettings(editorState), targetEnvironment: templatizedDownload ? undefined : downloadEnv, enableJavascript: enableJavascript, enableJourneyInsights: journeyInsightsSettings.enableJourneyInsights, addRandomPolicyIdTail: false}, editorState.templatization);
        let dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(rawXMLoutput.policyXML);
        dlElements.forEach((dlmId) => {
            // @ts-ignore
            dlParentRef.current!.removeChild(document.getElementById(dlmId));
        });
        let newDles: string[] = [];
        let dlAnchorElem: any = document.createElement("a");
        dlAnchorElem.setAttribute('id', `mdDownloadAnchor-basePolicy`);
        dlAnchorElem.setAttribute('href', dataStr);
        dlAnchorElem.setAttribute('download', 'basePolicy.xml');
        // Have to manually ignore these lines to handle refs and call DOM APIs
        newDles.push(`mdDownloadAnchor-basePolicy`);
        // @ts-ignore
        dlParentRef.current!.appendChild(dlAnchorElem);
        dlAnchorElem.click();
        rawXMLoutput.relyingParties.forEach((rpr, idx) => {
            let rpAnchorElem: any = document.createElement("a");
            let rpDataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(rpr.xml);
            rpAnchorElem.setAttribute('id', `mdDownloadAnchor-${idx}`);
            rpAnchorElem.setAttribute('href', rpDataStr);
            rpAnchorElem.setAttribute('download', `${rpr.policyName}.xml`);
            newDles.push(`mdDownloadAnchor-${idx}`)
            // @ts-ignore
            dlParentRef.current!.appendChild(rpAnchorElem);
            rpAnchorElem.click();
        });
        if (templatizedDownload) {
            let appSettingsJson = generateAppSettingsJsonString(rawXMLoutput.buildSettings.templatization, envsToInclude);
            let asAnchorElem: any = document.createElement("a");
            let asDataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(appSettingsJson);
            asAnchorElem.setAttribute('id', `mdDownloadAnchor-appSettings`);
            asAnchorElem.setAttribute('href', asDataStr);
            asAnchorElem.setAttribute('download', `appsettings.json`);
            newDles.push(`mdDownloadAnchor-appSettings`);
            // @ts-ignore
            dlParentRef.current!.appendChild(asAnchorElem);
            asAnchorElem.click();
        }
        setDlElements(newDles);
    } catch (error) {
        console.error(error);
    }
}

// Some utility methods used for environment state validation.



const getEnabledEnvsForAppSettings = (envsForAppSettings: { [index: string]: any }) => {
    return Object.keys(envsForAppSettings).filter((envId) => {
        return envsForAppSettings[envId]?.included;
    });
}

const selectedEnvsExist = (editorState: any, templatizedDownload: boolean, envsForAppSettings: { [index: string]: any }, downloadEnv: string) => {
    if (templatizedDownload) {
        return getEnabledEnvsForAppSettings(envsForAppSettings).every((envId) => {
            return envExists(editorState, envId);
        });
    } else {
        return envExists(editorState, downloadEnv);
    }
}

const selectedEnvsHaveEmptySettings = (editorState: any, templatizedDownload: boolean, envsForAppSettings: { [index: string]: any }, downloadEnv: string) => {
    if (templatizedDownload) {
        return getEnabledEnvsForAppSettings(envsForAppSettings).some((envId) => {
            return envHasEmptySettings(editorState, envId);
        });
    } else {
        return envHasEmptySettings(editorState, downloadEnv);
    }
}

export const PolicyDownloadUIModalComponent = ({
    closeComponent,
    enableJavascript,
    setEnableJavascript,
    journeyInsightsSettings,
    setJourneyInsightsSettings,
    templatizedDownload,
    setTemplatizedDownload,
    envsForAppSettings,
    setEnvsForAppSettings,
    downloadEnv,
    setDownloadEnv
}: PolicyDownloadUIModalProps) => {
    const classes: any = jsonEditorStyles();
    const dlParentRef = useRef(null);
    const [dlElements, setDlElements] = useState<string[]>([]);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
    const [allowEmptySettings, setAllowEmptySettings] = useState<boolean>(false);
    const editorState = useSelector(getJsonEditor);
    const sessionId = useSelector(getSessionId);
    return (
        <Modal
            disablePortal
            disableEnforceFocus
            disableAutoFocus
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeComponent();
                }
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            className={classes.modalStyle}
        >
            <div className={classes.popup}>
                <div className={cn(classes.header)}>
                    <div className={cn(classes.flexRow, classes.alignCenter)}>Download Policies</div>
                    <div>
                        <ClearOutlinedIcon
                            onClick={() => {
                                closeComponent();
                            }}
                            className={classes.delete}
                        />
                    </div>
                </div>
                <Box sx={{ display: 'flex', flexDirection: 'column', mx: 2 }}>
                    <div id="multiDownloadParent" ref={dlParentRef} style={{ display: 'none'}}></div>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{ width: '50%', my: 2, py: 2 }}
                        onClick={(e) => {
                            executePolicyDownload(dlElements, setDlElements, dlParentRef, editorState, sessionId, templatizedDownload, downloadEnv, Object.keys(envsForAppSettings).filter((k) => { return envsForAppSettings[k].included }), enableJavascript, journeyInsightsSettings);
                        }}
                        disabled={
                            (!selectedEnvsExist(editorState, templatizedDownload, envsForAppSettings, downloadEnv)) ||
                            ((!allowEmptySettings) && selectedEnvsHaveEmptySettings(editorState, templatizedDownload, envsForAppSettings, downloadEnv)) ||
                            validationResultsContainErrors(editorState)
                        }
                    >
                        Download
                    </Button>
                    {(!allowEmptySettings) && 
                        selectedEnvsExist(editorState, templatizedDownload, envsForAppSettings, downloadEnv) &&
                        selectedEnvsHaveEmptySettings(editorState, templatizedDownload, envsForAppSettings, downloadEnv) && (
                        <Typography color="red">
                            An environment that would be used for policy or appSettings file generation has no value for one or more app settings
                        </Typography>
                    )}
                    {(validationResultsContainErrors(editorState.validationResults)) && (
                        <Typography color="red">
                            Flowcharts contain one or more errors
                        </Typography>
                    )}
                    <Box sx={{ my: 2 }}>
                        <Accordion
                            expanded={showAdvancedSettings}
                            onChange={(e: any, isExpanded: boolean) => {
                                setShowAdvancedSettings(isExpanded);
                            }}
                            sx={{ width: "50%", text: 'secondary' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>Advanced Download Settings</AccordionSummary>
                            <AccordionDetails>
                                <div className={classes.flexCol}>
                                    <FormControlLabel
                                        sx={{ my: 1, width: '300px' }}
                                        label='Download for Specific Environment'
                                        control={
                                            <Checkbox
                                                checked={!templatizedDownload}
                                                disabled={Object.keys(editorState.templatization.environments).length == 0}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setTemplatizedDownload(!templatizedDownload);
                                                }}
                                            />
                                        }
                                    />
                                    {templatizedDownload && (
                                        <FormControl sx={{my: '10px'}}>
                                            <InputLabel id='policyDownloadETIlabel'>Environments to include in appSettings file</InputLabel>
                                            <Select
                                                labelId='policyDownloadETIlabel'
                                                id='policyDownloadETImenu'
                                                multiple
                                                size="small"
                                                // @ts-ignore
                                                value={Object.keys(envsForAppSettings).filter((ek) => { return envsForAppSettings[ek].included; })}
                                                renderValue={(v) => "Select environments..."}
                                                input={<OutlinedInput label="Environments to include in appSettings file" />}
                                                onChange={(e: SelectChangeEvent<string[]>) => {
                                                    const _efa = cloneDeep(envsForAppSettings);
                                                    if (typeof e.target.value == 'string') {
                                                        const rArr = e.target.value.split(',');
                                                        Object.keys(_efa).forEach((envId: string) => {
                                                            if (rArr.includes(envId)) {
                                                                _efa[envId].included = true;
                                                            } else {
                                                                _efa[envId].included = false;
                                                            }
                                                        });
                                                    } else {
                                                        Object.keys(_efa).forEach((envId: string) => {
                                                            if (e.target.value.includes(envId)) {
                                                                _efa[envId].included = true;
                                                            } else {
                                                                _efa[envId].included = false;
                                                            }
                                                        });
                                                    }
                                                    setEnvsForAppSettings(_efa);
                                                }}
                                            >
                                                {Object.keys(envsForAppSettings).map((envId) => {
                                                    return (
                                                        <MenuItem key={envId} value={envId}>
                                                            <Checkbox checked={envsForAppSettings[envId].included} />
                                                            <ListItemText primary={envsForAppSettings[envId].label} />
                                                        </MenuItem>
                                                    )
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}
                                    {(!templatizedDownload) && (
                                        <SelectComponent
                                            changeHandler={(e: SelectChangeEvent) => {
                                                setDownloadEnv(e.target.value);
                                            }}
                                            data={{
                                                value: downloadEnv,
                                                label: 'Environment',
                                                options: Object.keys(editorState.templatization.environments).reduce((vs: OptionProps[], ke: string) => { vs.push({ value: ke, label: editorState.templatization.environments[ke].name }); return vs }, []),
                                                style: { marginTop: '10px', marginBottom: '10px'}
                                            }}
                                        />
                                    )}
                                    <FormControlLabel
                                        sx={{ my: 1, width: '300px' }}
                                        label='Force enabling of Javascript'
                                        control={
                                            <Checkbox
                                                checked={enableJavascript}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setEnableJavascript(!enableJavascript);
                                                }}
                                            />
                                        }
                                    />
                                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '-11px'}}>
                                        <FormControlLabel
                                            sx={{ m: 0, width: '300px' }}
                                            label='Enable Journey Insights'
                                            control={
                                                <Checkbox
                                                    checked={journeyInsightsSettings.enableJourneyInsights}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setJourneyInsightsSettings({
                                                            ...journeyInsightsSettings,
                                                            enableJourneyInsights: !journeyInsightsSettings.enableJourneyInsights
                                                        });
                                                    }}
                                                />
                                            }
                                        />
                                        <TooltipComponent
                                            title='Journey Insights Instance'
                                            desc='The Application Insights instance that will be used for Journey Insights for a particular tenant corresponds to the instrumentation key set as the value of the "applicationInsightsInstrumentationKey" App Setting. Whether the Journey Insights client-side scripts are enabled can be configured by setting the "applicationInsightsClientEnabled" App Setting.'
                                        />
                                    </div>
                                    <div className={classes.flexRow} style={{alignItems: 'center'}}>
                                        <FormControlLabel
                                            sx={{ my: 1, width: '300px' }}
                                            label='Allow empty appSettings fields'
                                            control={
                                                <Checkbox
                                                    checked={allowEmptySettings}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setAllowEmptySettings(!allowEmptySettings);
                                                    }}
                                                />
                                            }
                                        />
                                        <TooltipComponent
                                            title='Allowing empty fields'
                                            desc='If allowed, "specific environment" policy XML will generate with "{settings:...}" tags intact for missing fields, and appSettings.json will be generated with entries missing in environments for fields that do not contain a value.'
                                        />
                                    </div>
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            </div>
        </Modal>
    )
}

